import { pageTypeConstants as pageTypes } from '../constants/pageTypeConstants';

export const CATEGORY_LEVEL_1 = 'categoryL1';
export const CATEGORY_LEVEL_2 = 'categoryL2';
const CATEGORY_LEVEL_3 = 'categoryL3';
const CATEGORY_LEVEL_4 = 'categoryL4';

export const categorySortOrder = [
    CATEGORY_LEVEL_1,
    CATEGORY_LEVEL_2,
    CATEGORY_LEVEL_3,
    CATEGORY_LEVEL_4,
] as const;
type CategoryName = (typeof categorySortOrder)[number];

type Filter = {
    readonly name: string | null;
    readonly values: ReadonlyArray<{
        readonly code?: string | null;
        readonly displayName: string | null;
        readonly urlLabel: string | null;
    } | null> | null;
} | null;
export type Filters = ReadonlyArray<Filter> | null;

export const getCapitalizedFirstLetter = (word?: string | null): string => {
    if (!word) {
        return '';
    }
    const [firstLetter] = word;
    const restOfLetters = word.slice(1);
    return `${firstLetter.toUpperCase()}${restOfLetters.toLowerCase()}`;
};

export const getSortedDescCategories = (categories: Filters): Filter[] => {
    const clonedCategories = (categories || []).slice();
    clonedCategories.sort(
        (a, b) =>
            categorySortOrder.indexOf(b?.name as CategoryName) -
            categorySortOrder.indexOf(a?.name as CategoryName)
    );
    return clonedCategories;
};

// Return the deepest-nested category, closest to L4
export const getLowestCategory = (categories: Filters): Filter => {
    const sorted = getSortedDescCategories(categories);
    return sorted?.[0] || null;
};

export const isCategory = (filter: Filter): boolean =>
    categorySortOrder.includes(filter?.name as CategoryName);

export const getCategoryUrlLabel = (category: Filter): string =>
    category?.values?.[0]?.urlLabel || '';

export const getCategoryCode = (category: Filter): string => category?.values?.[0]?.code || '';

export const getCategories = (filters: Filters): Filter[] => (filters || []).filter(isCategory);

export const getPageLevel = (category: Filter): string =>
    category?.name ? category.name.replace('category', '') : ''; // strip the category from name so categoryL1 => L1

export const getCategory = (
    categories: Filters,
    categoryName: CategoryName = CATEGORY_LEVEL_1
): Filter => categories?.filter(category => categoryName === category?.name)?.[0] || null;

export const getAvailableL1Categories = (filters: Filters): Filter => {
    const availableCategories = getCategories(filters);
    return getCategory(availableCategories);
};

export const getBackToCategory = (appliedFilters: Filters = []): Filter => {
    const appliedCategories = getCategories(appliedFilters);
    // Should return the second to last category Applied
    return appliedCategories[appliedCategories.length - 2] || null;
};

export const hasAppliedCategories = (appliedFilters: Filters = []): boolean => {
    const appliedCategories = getCategories(appliedFilters);
    return !!appliedCategories && !!appliedCategories.length;
};

const hasSingleAvailableL1Category = (filters: Filters = []): boolean => {
    const availableL1Categories = getAvailableL1Categories(filters);
    const numAvailableL1Categories = availableL1Categories?.values || [];
    return !!availableL1Categories && numAvailableL1Categories.length === 1;
};

export const shouldShowBackToAllCategories = (
    { pageType, isAttributePage }: { pageType?: string | null; isAttributePage?: boolean | null },
    filters: Filter[]
): boolean => {
    return (
        !(pageType === pageTypes.BROWSE || isAttributePage) &&
        !hasSingleAvailableL1Category(filters)
    );
};

export const getCurrentCategory = (
    appliedFilters: Filter[] = [],
    filters: Filter[] = []
): Filter => {
    const availableCategories = getCategories(filters);
    const currentCategory = hasAppliedCategories(appliedFilters)
        ? // If categories applied or non-vertical page results set belongs to single l1,
          // current category is deepest category level user can filter on
          availableCategories[availableCategories.length - 1]
        : // If non-vertical page with no filters has results belonging to multiple l1s,
          // current categories are the l1s
          getAvailableL1Categories(filters);

    return currentCategory;
};

export const getPreviouslyAppliedCategory = (appliedFilters: Filters = []): Filter => {
    let previouslyAppliedCategory: Filter | null = null;
    if (hasAppliedCategories(appliedFilters)) {
        const appliedCategories = getCategories(appliedFilters);
        // If categories applied, previous category is last category level applied
        previouslyAppliedCategory = appliedCategories[appliedCategories.length - 1];
    }

    // If no filters are applied and results set container multiple L1s,
    // there is no previously applied category
    return previouslyAppliedCategory;
};

export const isCreatorCategoryOrSearchPage = ({ uriRef }: { uriRef?: string | null }): boolean => {
    return /^(\/(de|fr|it|es))?\/(art|furniture|fashion|jewelry|search|creators)\//.test(
        uriRef || ''
    );
};
